import { createContext, useContext, useEffect, useState, useCallback, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";

interface Club {
  id: string;
  name: string;
  slug: string;
  crest_url: string | null;
  primary_colour: string | null;
  secondary_colour: string | null;
  location: string | null;
  plan: string;
  plan_status: string;
  site_status: string | null;
}

interface ClubContextType {
  club: Club | null;
  loading: boolean;
  error: string | null;
  isMarketingSite: boolean;
  isCustomDomain: boolean;
  isImpersonating: boolean;
  userClubs: Club[];
  setActiveClub: (slug: string) => void;
  clearActiveClub: () => void;
}

const OVERRIDE_KEY = "admin_club_override";

const PLATFORM_HOSTNAMES = new Set([
  "uptheclub.com",
  "www.uptheclub.com",
  "uptheclub.co.uk",
  "www.uptheclub.co.uk",
]);

const ClubContext = createContext<ClubContextType>({
  club: null,
  loading: true,
  error: null,
  isMarketingSite: true,
  isCustomDomain: false,
  isImpersonating: false,
  userClubs: [],
  setActiveClub: () => {},
  clearActiveClub: () => {},
});

function resolveSlugFromHostname(): string | null {
  const override = sessionStorage.getItem(OVERRIDE_KEY);
  if (override) return override;

  const params = new URLSearchParams(window.location.search);
  const clubParam = params.get("club");
  if (clubParam) return clubParam;

  const hostname = window.location.hostname;

  if (PLATFORM_HOSTNAMES.has(hostname)) return null;

  if (hostname.endsWith(".uptheclub.com")) {
    const sub = hostname.replace(".uptheclub.com", "");
    if (sub && sub !== "www" && sub !== "app") return sub;
  }

  return null;
}

/**
 * Detect if the current hostname is a custom domain (not uptheclub.com, not localhost, not lovable.app).
 * Returns the hostname or null.
 */
function detectCustomDomain(): string | null {
  const hostname = window.location.hostname;
  if (
    hostname === "localhost" ||
    PLATFORM_HOSTNAMES.has(hostname) ||
    hostname.endsWith(".uptheclub.com") ||
    hostname.endsWith(".lovable.app") ||
    hostname.endsWith(".lovableproject.com") ||
    hostname.endsWith(".webcontainer.io") ||
    hostname === "127.0.0.1"
  ) {
    return null;
  }
  // Strip "www." prefix for matching
  return hostname.replace(/^www\./, "");
}

export const ClubProvider = ({ children }: { children: ReactNode }) => {
  const [club, setClub] = useState<Club | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [userClubs, setUserClubs] = useState<Club[]>([]);
  const [overrideSlug, setOverrideSlug] = useState<string | null>(
    sessionStorage.getItem(OVERRIDE_KEY)
  );

  const slug = overrideSlug || resolveSlugFromHostname();
  const customDomain = !slug ? detectCustomDomain() : null;
  const isImpersonating = !!sessionStorage.getItem(OVERRIDE_KEY);

  const setActiveClub = useCallback((newSlug: string) => {
    sessionStorage.setItem(OVERRIDE_KEY, newSlug);
    setOverrideSlug(newSlug);
  }, []);

  const clearActiveClub = useCallback(() => {
    sessionStorage.removeItem(OVERRIDE_KEY);
    setOverrideSlug(null);
    setClub(null);
    setUserClubs([]);
  }, []);

  // Resolve club from slug (hostname/override/query param)
  useEffect(() => {
    if (!slug) {
      // No slug — will try membership resolution below
      return;
    }

    setLoading(true);
    const fetchClub = async () => {
      const { data, error: fetchError } = await supabase
        .from("clubs_public")
        .select("id, name, slug, crest_url, primary_colour, secondary_colour, location, plan, plan_status, site_status")
        .eq("slug", slug)
        .maybeSingle();

      if (fetchError || !data) {
        setError("Club not found");
      } else {
        setClub(data as Club);
        setError(null);
      }
      setLoading(false);
    };

    fetchClub();
  }, [slug]);

  // Resolve club from custom domain
  useEffect(() => {
    if (slug || !customDomain) return;

    setLoading(true);
    const fetchByDomain = async () => {
      const { data, error: fetchError } = await supabase
        .from("clubs_public")
        .select("id, name, slug, crest_url, primary_colour, secondary_colour, location, plan, plan_status, site_status")
        .or(`custom_domain.eq.${customDomain},custom_domain.eq.www.${customDomain}`)
        .limit(1)
        .maybeSingle();

      if (fetchError || !data) {
        setLoading(false);
        return;
      }
      setClub(data as Club);
      setError(null);
      setLoading(false);
    };

    fetchByDomain();
  }, [slug, customDomain]);

  // Auto-resolve club from memberships when no slug is available
  useEffect(() => {
    if (slug || customDomain) return; // Already resolved from slug or custom domain

    let cancelled = false;

    const resolveFromMemberships = async () => {
      setLoading(true);

      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user || cancelled) {
        setLoading(false);
        return;
      }

      const { data: memberships } = await supabase
        .from("club_memberships")
        .select("club_id, clubs(id, name, slug, crest_url, primary_colour, secondary_colour, location, plan, plan_status, site_status)")
        .eq("user_id", session.user.id)
        .eq("status", "approved");

      if (cancelled) return;

      if (memberships && memberships.length > 0) {
        const clubs = memberships
          .map((m: any) => m.clubs)
          .filter(Boolean) as Club[];

        setUserClubs(clubs);

        if (clubs.length === 1) {
          setClub(clubs[0]);
        }
        // If multiple clubs, leave club=null — DashboardOverview will show picker
      }

      setLoading(false);
    };

    resolveFromMemberships();

    // Also listen for auth changes to re-resolve
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session?.user) {
        resolveFromMemberships();
      } else {
        setClub(null);
        setUserClubs([]);
        setLoading(false);
      }
    });

    return () => {
      cancelled = true;
      subscription.unsubscribe();
    };
  }, [slug]);

  const isMarketingSite = !slug && !customDomain && !club;
  const isCustomDomain = !!customDomain;

  return (
    <ClubContext.Provider value={{ club, loading, error, isMarketingSite, isCustomDomain, isImpersonating, userClubs, setActiveClub, clearActiveClub }}>
      {children}
    </ClubContext.Provider>
  );
};

export const useClubContext = () => useContext(ClubContext);
